import { FC, useEffect, useId, useMemo, useState } from "react";
import { FormControl, InputLabel, Select, SelectChangeEvent } from "@mui/material";
import { useAccessRight, useTranslate } from "@/hooks";
import { TCustomSelect, TSelectValue } from "./CustomSelect.props";
import Hint from "../../HOC/Hint/Hint";
import { CustomMenuItem } from "@/ui";

const CustomSelect: FC<TCustomSelect> = ({
	variantsList,
	hint = "",
	mt = "4px",
	title,
	setState,
	elementWidth,
	height = 40,
	width = 120,
	valueIndex = 0,
	value: controlledValue,
	children,
	...props
}) => {
	const id = useId();
	const isAccess = useAccessRight();
	const translate = useTranslate();

	const isControlled = controlledValue !== undefined;
	const [variant, setVariant] = useState<TSelectValue>(isControlled ? controlledValue : variantsList[valueIndex]?.value ?? "");

	useEffect(() => {
		if (isControlled) setVariant(controlledValue);
	}, [controlledValue, isControlled]);

	useEffect(() => {
		if (controlledValue !== undefined) {
			setVariant(controlledValue);
		} else {
			setVariant(variantsList[valueIndex]?.value ?? "");
		}
	}, [controlledValue, variantsList, valueIndex]);

	const selectWidth = useMemo(() => {
		if (!variantsList.length) return 200;
		const longestTitle = variantsList.reduce((longest, item) => (translate(item.title).length > translate(longest.title).length ? item : longest));
		const textLength = translate(longestTitle.title).length;
		const estimatedWidth = textLength * 8.5 + 40;
		return Math.min(Math.max(estimatedWidth, 120), 300);
	}, [variantsList, translate]);

	const handleChange = (e: SelectChangeEvent<TSelectValue>) => {
		const val = e.target.value as TSelectValue;

		setVariant(val);
		setState?.(val as string);
	};

	return (
		<Hint hint={hint}>
			<FormControl
				sx={{
					mt,
					width: elementWidth === "full" ? "100%" : elementWidth === "fixed" ? selectWidth : width,
					"& .MuiInputBase-root": {
						width: "100%",
					},
				}}
				fullWidth
			>
				<InputLabel
					id={`${id}-label`}
					sx={{
						top: "-8px",
						"&.Mui-focused, &.MuiFormLabel-filled": {
							top: "1px",
						},
					}}
				>
					{props.label ?? title}
				</InputLabel>
				<Select<TSelectValue>
					{...props}
					fullWidth
					labelId={`${id}-label`}
					id={`${id}-select`}
					value={variant}
					label={props.label ?? title}
					onChange={handleChange}
					sx={{
						width: elementWidth === "full" ? "100%" : elementWidth === "fixed" ? selectWidth : width,
						height,
						minHeight: height,
						".MuiSelect-select": {
							height,
							paddingY: "0 !important",
							display: "flex",
							alignItems: "center",
							lineHeight: `${height}px`,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							flex: 1,
							width: "100%",
						},

						"& .MuiOutlinedInput-notchedOutline legend": {
							padding: 0,
							fontSize: "10px",
							lineHeight: "1.2",
						},
						"& .MuiOutlinedInput-notchedOutline legend > span": {
							display: "inline-block",
							fontSize: "10px",
							lineHeight: "1.2",
						},
					}}
				>
					{children}
					{variantsList
						.filter((el) => (el.usedEndpoints ? isAccess(el.usedEndpoints) : true))
						.map((el) => (
							<CustomMenuItem value={el.value} key={el.value}>
								{translate(el.title)}
							</CustomMenuItem>
						))}
				</Select>
			</FormControl>
		</Hint>
	);
};

export default CustomSelect;
